// @ts-nocheck
import React, { useState } from 'react';
import { Calendar, CalendarCheck, Wallet, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMobileAuth } from './hooks/useMobileAuth';
import PublicEventsScreen from './screens/PublicEventsScreen';
import MyEventsScreen from './screens/MyEventsScreen';
import MyWalletScreen from './screens/MyWalletScreen';
import ProfileScreen from './screens/ProfileScreen';
import MobileAuthScreen from './screens/MobileAuthScreen';

type TabType = 'my-events' | 'events' | 'wallet' | 'profile';

const tabs = [
  { id: 'my-events' as TabType, label: 'My Events', icon: CalendarCheck, requiresAuth: true },
  { id: 'events' as TabType, label: 'Events', icon: Calendar, requiresAuth: false },
  { id: 'wallet' as TabType, label: 'Wallet', icon: Wallet, requiresAuth: true },
  { id: 'profile' as TabType, label: 'Profile', icon: User, requiresAuth: false },
];

const MobileEventsApp: React.FC = () => {
  // Use mobile-specific auth hook - NO role-based redirects
  const { user, profile, loading, signIn, signOut } = useMobileAuth();
  const [activeTab, setActiveTab] = useState<TabType>('events');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // Handle tab selection - check if auth is required
  const handleTabSelect = (tabId: TabType) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab?.requiresAuth && !user) {
      // Show login prompt for auth-required tabs
      setShowLoginPrompt(true);
      return;
    }
    setActiveTab(tabId);
    if (tabId === 'events' || tabId === 'my-events') {
      setSelectedEventId(null);
    }
  };

  // Handle login from prompt
  const handleLoginFromPrompt = async (email: string, password: string) => {
    const success = await signIn(email, password);
    if (success) {
      setShowLoginPrompt(false);
      // Navigate to the my-events tab after successful login
      setActiveTab('my-events');
    }
    return success;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show login prompt modal for auth-required tabs
  if (showLoginPrompt) {
    return (
      <div className="relative h-screen">
        <MobileAuthScreen 
          onLogin={handleLoginFromPrompt}
          onBack={() => setShowLoginPrompt(false)}
          showBackButton={true}
          onRegistrationSuccess={() => {
            setShowLoginPrompt(false);
            setActiveTab('my-events');
          }}
        />
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'my-events':
        // This should only be reachable when logged in
        if (!user) {
          setShowLoginPrompt(true);
          return null;
        }
        return (
          <MyEventsScreen 
            selectedEventId={selectedEventId}
            onSelectEvent={setSelectedEventId}
            onBack={() => setSelectedEventId(null)}
            userEmail={user?.email}
            profileId={profile?.id}
          />
        );
      case 'events':
        return (
          <PublicEventsScreen 
            selectedEventId={selectedEventId}
            onSelectEvent={setSelectedEventId}
            onBack={() => setSelectedEventId(null)}
            userEmail={user?.email}
            profileId={profile?.id}
          />
        );
      case 'wallet':
        // This should only be reachable when logged in (checked in handleTabSelect)
        if (!user) {
          setShowLoginPrompt(true);
          return null;
        }
        return (
          <MyWalletScreen 
            userId={user.id}
            profileId={profile?.id}
            onNavigateToEvents={() => setActiveTab('events')} 
          />
        );
      case 'profile':
        // Profile tab - shows login screen or profile based on auth state
        if (!user) {
          return (
            <MobileAuthScreen 
              onLogin={signIn}
              onRegistrationSuccess={() => setActiveTab('my-events')}
            />
          );
        }
        return <ProfileScreen user={user} profile={profile} onSignOut={signOut} onLogin={signIn} />;
      default:
        return (
          <PublicEventsScreen 
            selectedEventId={selectedEventId}
            onSelectEvent={setSelectedEventId}
            onBack={() => setSelectedEventId(null)}
            userEmail={user?.email}
            profileId={profile?.id}
          />
        );
    }
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto pb-16">
        {renderTabContent()}
      </main>

      {/* Bottom Tab Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border safe-area-bottom">
        <div className="flex justify-around items-center h-16">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => handleTabSelect(tab.id)}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 h-full transition-colors relative",
                  isActive 
                    ? "text-primary" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon size={22} strokeWidth={isActive ? 2.5 : 2} />
                <span className="text-[10px] mt-1 font-medium">{tab.label}</span>
                {/* Show lock indicator for auth-required tabs when not logged in */}
                {tab.requiresAuth && !user && (
                  <div className="absolute top-1 right-1/4 w-2 h-2 bg-amber-500 rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default MobileEventsApp;
